/**
 * Theme configuration for WorkOrder app
 * This file defines colors, spacing, typography, and other common design elements
 */
import { Platform } from 'react-native';

// Light theme colors
export const lightColors = {
	// Primary colors
	primary: '#2196f3',
	primaryDark: '#1976d2',
	primaryLight: '#bbdefb',

	// Secondary colors
	secondary: '#ff9800',
	secondaryDark: '#f57c00',
	secondaryLight: '#ffe0b2',

	// UI colors
	background: '#f5f5f5',
	card: '#ffffff',
	text: '#333333',
	textSecondary: '#666666',
	textLight: '#999999',
	textWhite: '#ffffff',
	textDark: '#000000',
	border: '#dddddd',
	divider: '#eeeeee',

	// Status colors
	success: '#4caf50',
	successLight: '#c8e6c9',
	// Green 800, not 600: this is the LABEL colour for outlined/text success buttons, and
	// #388e3c measured 4.2:1 on white — just under AA. This clears it at 5.1:1.
	successDark: '#2e7d32',
	warning: '#ff9800',
	warningDark: '#f57c00',
	error: '#bc3433',
	errorLight: '#ffcdd2',
	errorDark: '#d32f2f',
	info: '#2196f3',
	infoLight: '#bbdefb',
	infoDark: '#1976d2',
	purple: '#9c27b0',
	purpleDark: '#7b1fa2',
	purpleLight: '#e1bee7',
	yellow: '#ffff00',

	// Priority colors
	low: '#4caf50',    // Green
	lowLight: '#c8e6c9', // Light green
	medium: '#FFC107', // Amber
	mediumLight: '#FFF197', // Light Yellow
	high: '#ff9800', // Orange
	highLight: '#ffe0b2', // Orange light
	critical: '#f44336',   // Red
	criticalLight: '#ffcdd2', // Light red

	// Links
	link: '#2196f3',

	surface: '#f5f5f5',
	outline: '#e0e0e0',
	shadow: '#000000',
	scrim: '#000000',
};

// Dark theme colors
export const darkColors = {
	// Primary colors
	primary: '#90caf9',
	primaryDark: '#42a5f5',
	primaryLight: '#e3f2fd',

	// Secondary colors
	secondary: '#ffcc80',
	secondaryDark: '#ffb74d',
	secondaryLight: '#fff3e0',

	// UI colors
	background: '#121212',
	card: '#1e1e1e',
	text: '#ffffff',
	textSecondary: '#b3b3b3',
	textLight: '#666666',
	textWhite: '#ffffff',
	textDark: '#ffffff',
	border: '#333333',
	divider: '#2c2c2c',

	// Status colors
	success: '#81c784',
	successLight: '#4caf50',
	successDark: '#388e3c',
	warning: '#ffb74d',
	warningDark: '#ff9800',
	error: '#e57373',
	errorLight: '#ef5350',
	errorDark: '#d32f2f',
	info: '#64b5f6',
	infoLight: '#2196f3',
	infoDark: '#1976d2',
	purple: '#ba68c8',
	purpleDark: '#9c27b0',
	purpleLight: '#f3e5f5',
	yellow: '#fff176',

	// Priority colors
	low: '#81c784',    // Green
	lowLight: '#4caf50', // Light green
	medium: '#ffffff', // White
	mediumLight: '#ffffff', // White
	high: '#ffb74d', // Orange
	highLight: '#ffe0b2', // Orange light
	critical: '#e57373',   // Red
	criticalLight: '#ffcdd2', // Light red

	// Links
	link: '#90caf9',

	surface: '#1e1e1e',
	outline: '#333333',
	shadow: '#000000',
	scrim: '#000000',
};

export const spacing = {
	xs: 4,
	s: 8,
	m: 16,
	l: 24,
	xl: 32,
	xxl: 48,
};

export const typography = {
	fontSize: {
		small: 12,
		body: 14,
		button: 14,
		title: 16,
		h6: 18,
		h5: 20,
		h4: 24,
	},
	fontWeight: {
		regular: '400',
		medium: '500',
		bold: '700',
	},
	fontFamily: Platform.OS === 'web'
		? '"Segoe UI", "Times New Roman", -apple-system, BlinkMacSystemFont, Roboto, sans-serif'
		: 'System', // React Native will use the system default font
};

/**
 * Dark amber — work waiting on a REVIEWER.
 *
 * Deliberately distinct from the error red, which this app reserves for things that are
 * actually broken: a queue to work through is normal, not a fault. Shared because it had
 * been copied into five files (the drawer badge, the improvements rail, the submissions
 * and needs-review screens, and the needs-info composer) and any drift between them would
 * read as two different states rather than one.
 */
export const REVIEW_ACCENT = '#b8860b';

// Theme type for type safety
export type ThemeColors = typeof lightColors;

// Export colors for backward compatibility
export const colors = lightColors;

// Function to get colors based on theme mode
export function getColors(isDark: boolean = false): ThemeColors {
	return isDark ? darkColors : lightColors;
}

export const layout = {
	borderRadius: {
		small: 4,
		medium: 8,
		large: 12,
	},
	elevation: {
		small: {
			elevation: 1,
		},
		medium: {
			elevation: 2,
		},
		large: {
			elevation: 4,
		},
	},
};

// Default screen container styling
export const container = {
	screen: {
		flex: 1,
		backgroundColor: colors.background,
		padding: spacing.m,
	},
	scrollView: {
		flex: 1,
		backgroundColor: colors.background,
		padding: spacing.m,
	},
};
